/**
 * `growth/gate` — ad eligibility. **PURE** function (no SDK, no clock,
 * no storage): it receives state and returns `{ allow, reason }`.
 *
 * Implements the COMPLETE frequency-capping table from the ads strategy:
 *
 * | Rule                                   | Default |
 * |-----------------------------------------|---------|
 * | Interstitial per session               | 1, and only with ≥1 value unit |
 * | Interstitial per day                   | 3 |
 * | Minimum gap between interstitials      | ≥10 min |
 * | First interstitial ever                | never in the 1st session |
 * | Native per screen                      | 1 |
 * | Visible banner                         | 1 |
 * | Rewarded                               | 3 rewards/day PER TYPE |
 * | Ignored rewarded offer                 | 2/day, with backoff to 1 after 3 days |
 * | Global interruption budget             | ≤1 interstitial / 10 active min |
 * | Premium                                | 0 ads, immediate |
 *
 * `reason` is a stable code (not UI text): it feeds telemetry and is what the
 * tests assert.
 *
 * DAILY counters arrive already normalized by `AdCapsStore` (which resets when
 * the day changes): the gate deliberately knows nothing about time zones.
 *
 * **The table's NUMBERS do not live here**: they are `DEFAULT_AD_CAP_POLICY` /
 * `CHILD_AD_CAP_POLICY` from `growth/regime`, together with the classification
 * of what is a non-negotiable minimum and what is an operational parameter.
 * This module only CONSUMES them: it does not merge policy.
 */

import {
  type AdCapCeiling,
  capToCeiling,
  CONFIGURABLE_AD_CAP_KEYS,
  effectiveAdCapPolicy,
  PORTFOLIO_AD_CAP_CEILING,
} from "./regime";
import type { AdCapPolicy, AdGateDecision, AdGateInput, AdGateReason, AdKind } from "./types";

/**
 * The four caps the back office can change live (`adcaps.*` from
 * CONFIG_OVER_CODE, served by `GET /api/config`). It is declared
 * STRUCTURALLY so the kit remains agnostic to the API package: the incoming
 * value is the `AdCapsSettings` DTO, but `growth/` does not need to know that.
 */
export interface AdCapsSettingsLike {
  interstitialPerSession: number;
  interstitialPerDay: number;
  rewardsPerDayPerType: number;
  offersPerDayPerPlacement: number;
}

/**
 * **Adapter for the "runtime configuration key" path**: translates the
 * `adcaps.*` values resolved by the SERVER into the gate policy.
 *
 * Real config-over-code: without this, values traveled in the DTO, were
 * audited and edited in the panel… while the client kept using
 * `DEFAULT_AD_CAP_POLICY`. It deliberately returns a `Partial` — overriding
 * only the four configurable keys and leaving the rest untouched, since they
 * are **code-level operational parameters** (gaps, interruption budget,
 * backoff) and are not changed from a panel.
 *
 * Two guardrails because this path comes from the network:
 *   1. a non-finite or negative value is IGNORED instead of breaking capping
 *      (it falls back to the code default, not to "no caps");
 *   2. the remainder is bounded by the **PRODUCT CEILING** (`ceiling`), which
 *      is one-way by portfolio decision: lowering is allowed, raising is not.
 *      The server already clamps it (`app_config::resolve::clamp`); this is the
 *      second line of defense in case a clone omits the clamp or a tampered DTO
 *      arrives.
 *
 * Mind the nature: this ceiling is **not** the tier's non-negotiable floor
 * (`effectiveAdCapPolicy` applies that) and **does not** reach operational
 * parameters. A clone with a different commercial promise passes its own
 * `ceiling` instead of forking the kit.
 *
 * Without config (offline guest, server down), it returns `{}`: the code
 * default applies, which is the historical behavior.
 */
export function adCapPolicyFromSettings(
  settings: AdCapsSettingsLike | undefined | null,
  ceiling: AdCapCeiling = PORTFOLIO_AD_CAP_CEILING,
): Partial<AdCapPolicy> {
  if (!settings) {
    return {};
  }
  const sane: Partial<AdCapPolicy> = {};
  for (const key of CONFIGURABLE_AD_CAP_KEYS) {
    const value = settings[key];
    if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
      sane[key] = value;
    }
  }
  return capToCeiling(sane, ceiling);
}

/** Formats allowed under the child regime (§6). */
const CHILD_ALLOWED_KINDS: ReadonlySet<AdKind> = new Set(["banner", "interstitial", "rewarded"]);

function deny(reason: AdGateReason): AdGateDecision {
  return { allow: false, reason };
}

const ALLOW: AdGateDecision = { allow: true, reason: "allow" };

function interstitialDecision(i: AdGateInput, p: AdCapPolicy): AdGateDecision {
  const { caps, session } = i;

  // The first-run rule is preserved in full: native/banner can live on day 1,
  // interstitial cannot. Burning the first session hurts retention (and LTV).
  if (session.isFirstEver && !p.allowInterstitialOnFirstSession) {
    return deny("first-session");
  }
  // An interstitial is legal only AFTER completing a value unit: that turns
  // "ad break" into a breather rather than an interruption.
  if (session.unitsCompleted < p.minUnitsCompleted) {
    return deny("no-value-unit");
  }
  if (caps.interstitialsThisSession >= p.interstitialPerSession) {
    return deny("session-cap");
  }
  if (caps.interstitialsToday >= p.interstitialPerDay) {
    return deny("daily-cap");
  }
  if (
    caps.lastInterstitialAt !== undefined &&
    i.now - caps.lastInterstitialAt < p.minGapBetweenInterstitialsMs
  ) {
    return deny("min-gap");
  }
  // Global budget: ≤1 interrupting impression per `interruptionBudgetMs` of
  // ACTIVE use. The first one in a session is free (0 × budget ≤ activeMs);
  // after that, each new interruption must be "earned" through real usage.
  if (caps.interstitialsThisSession * p.interruptionBudgetMs > session.activeMs) {
    return deny("interruption-budget");
  }
  return ALLOW;
}

function rewardedDecision(i: AdGateInput, p: AdCapPolicy): AdGateDecision {
  const { caps, placementId, rewardType } = i;

  // Anti-farming: the cap is per reward TYPE, not per placement — otherwise,
  // two placements granting the same reward would double the ceiling.
  const type = rewardType ?? placementId;
  if ((caps.rewardsToday[type] ?? 0) >= p.rewardsPerDayPerType) {
    return deny("reward-daily-cap");
  }

  // Backoff: if the user has ignored the offer for days, show it less.
  const ignored = caps.ignoredDays[placementId] ?? 0;
  const offerLimit =
    ignored >= p.ignoredDaysBeforeBackoff
      ? p.offersPerDayWhenBackedOff
      : p.offersPerDayPerPlacement;
  const offers = caps.offersToday[placementId] ?? 0;
  if (offers >= offerLimit) {
    return deny(ignored >= p.ignoredDaysBeforeBackoff ? "offer-backoff" : "offer-daily-cap");
  }
  return ALLOW;
}

/**
 * Should this ad be shown NOW?
 *
 * The denial order is deliberate: absolute conditions first (premium, task in
 * progress, minors), then capping. This way the `reason` sent to telemetry
 * states the REAL cause instead of "cap reached" when it was actually premium.
 */
export function adGate(input: AdGateInput): AdGateDecision {
  // The floor is NOT negotiable: what `input.policy` requests (runtime
  // `adcaps.*` config or code injection from the placement registry) can only
  // HARDEN the tier and child regimes. All composition lives in
  // `growth/regime` — policy is never manually merged here again.
  const policy: AdCapPolicy = effectiveAdCapPolicy({
    tier: input.tier,
    childDirected: input.user.child,
    requested: input.policy,
  });

  // 1. Premium removes ads from the ENTIRE app, immediately and without waiting
  //    for sync (the optimistic local flag applies after purchase).
  if (input.user.premium) {
    return deny("premium");
  }

  // 2. Never mid-task: active input, a running timer, a transaction in progress
  //    or an ongoing gesture. This overrides any capping schedule.
  if (input.inFlight) {
    return deny("in-flight");
  }

  // 3. Child regime.
  if (input.user.child) {
    if (!CHILD_ALLOWED_KINDS.has(input.kind)) {
      return deny("child-policy");
    }
    if (input.kind === "interstitial" && input.breakpoint !== "between-screens") {
      // Never inside gameplay: only the transition between screens.
      return deny("child-policy");
    }
    if (input.kind === "rewarded" && !input.user.adultGateCleared) {
      // Rewarded disabled by default: only after the parental gate.
      return deny("child-policy");
    }
  }

  switch (input.kind) {
    case "interstitial": {
      return interstitialDecision(input, policy);
    }
    case "rewarded": {
      return rewardedDecision(input, policy);
    }
    case "native": {
      return input.caps.nativesOnScreen >= policy.nativePerScreen ? deny("screen-cap") : ALLOW;
    }
    case "banner": {
      return input.caps.bannersVisible >= policy.bannersVisible ? deny("screen-cap") : ALLOW;
    }
    default: {
      return deny("child-policy");
    }
  }
}
